import {
  type AssignableProjectRole,
  CSRF_HEADER,
  type PrincipalOptions,
  type ProjectDetail,
  type ProjectGroupGrant,
  type ProjectMemberView,
  type ProjectRole,
  type ProjectRuleGrant,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Crown, Ellipsis, ListFilter, Plus, UserMinus, UserRound, Users } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Avatar } from "@/components/Avatar";
import { FilterChips } from "@/components/FilterChips";
import { ListRow } from "@/components/ListRow";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../../api.ts";
import { ok } from "../../api-errors.ts";
import { formatDate } from "../../i18n/format.ts";
import { useSession } from "../../session.tsx";
import { can, canChange } from "../capabilities.ts";
import {
  projectKeys,
  useMembers,
  usePrincipals,
  useProjectGroups,
  useProjectRuleGrants,
} from "../queries.ts";
import { RoleLabel, RolePicker } from "../RolePicker.tsx";
import { shortId } from "../readers.ts";
import { AddAccessDialog } from "./AddAccessDialog.tsx";
import { type AccessApi, type AccessKind, accessApi } from "./access-api.ts";
import { projectRefusal } from "./refusals.ts";
import { SettingsSection } from "./SettingsSection.tsx";

type Filter = AccessKind | "all";

/** One row of the access list: a person, a group grant or an attribute-rule grant. */
type Access =
  | { kind: "person"; id: string; name: string; role: ProjectRole; member: ProjectMemberView }
  | { kind: "group"; id: string; name: string; role: ProjectRole; grant: ProjectGroupGrant }
  | { kind: "rule"; id: string; name: string; role: ProjectRole; grant: ProjectRuleGrant };

const KIND_ICON = { group: Users, rule: ListFilter } as const;

/**
 * Who has a role in the project, in one list: people, directory groups and attribute rules,
 * each row with the same anatomy (tile, name and one-line subtitle, role, "…" actions). One
 * "Add" action opens a dialog for all three. With `members.manage` the caller manages people
 * and with `grants.manage` groups and rules; the owner's row is static (only a transfer moves
 * it). The server stays the authority, and its refusals are shown in plain words.
 */
export function MembersSection({ project }: { project: ProjectDetail }) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const manageMembers = canChange(project, "members.manage");
  const manageGrants = canChange(project, "grants.manage");
  // A change to the caller's own role (directly or through a group or rule) changes what the
  // whole project lets them see and do: everything under it is refetched, not only this list.
  // Not awaited: a settled change frees its controls at once, while the lists catch up.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: projectKeys.project(project.id) });
    void queryClient.invalidateQueries({ queryKey: projectKeys.all, exact: true });
  };
  return (
    <AccessList
      project={project}
      members={useMembers(project.id)}
      groups={useProjectGroups(project.id)}
      rules={useProjectRuleGrants(project.id)}
      principals={usePrincipals(manageGrants)}
      manageMembers={manageMembers}
      manageGrants={manageGrants}
      // The owner role of a personal project stays with its owning user: no transfer to offer.
      mayTransfer={canChange(project, "owner.transfer")}
      ownerOnlyNote={manageMembers && !can(project, "owner.transfer")}
      access={accessApi(project.id, csrfToken)}
      refresh={refresh}
      level={3}
    />
  );
}

/** A list as the access list reads it: a query's data and state. */
export interface ListState<T> {
  data: T[] | undefined;
  error: Error | null;
  isSuccess: boolean;
  isError: boolean;
  isFetching: boolean;
}

/**
 * The access list itself, fed by the caller: the project's own routes ({@link MembersSection})
 * or governance in the backoffice, which passes its own `access` and never transfers ownership.
 */
export function AccessList(props: {
  project: Pick<ProjectDetail, "id" | "visibility">;
  members: ListState<ProjectMemberView>;
  groups: ListState<ProjectGroupGrant>;
  rules: ListState<ProjectRuleGrant>;
  principals: { data: PrincipalOptions | undefined; error: Error | null };
  manageMembers: boolean;
  manageGrants: boolean;
  mayTransfer: boolean;
  /** Under the owner's row: only the owner may transfer ownership. */
  ownerOnlyNote: boolean;
  access: AccessApi;
  refresh: () => void;
  /** 3 under a settings group's heading; 2 (default) as a top-level section. */
  level?: 2 | 3;
}) {
  const { project, members, groups, rules, principals, manageMembers, manageGrants } = props;
  const { mayTransfer, access, refresh } = props;
  const { t } = useTranslation();
  const { userId, csrfToken } = useSession();
  const [filter, setFilter] = useState<Filter>("all");
  const [adding, setAdding] = useState(false);
  // The row the remove dialog names, kept while the dialog animates closed after a removal.
  const [removing, setRemoving] = useState<Access | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [transferring, setTransferring] = useState<ProjectMemberView | null>(null);
  // The actions button a dialog was opened from: its menu item is gone once the dialog closes.
  const opener = useRef<HTMLElement | null>(null);

  const isMe = (m: ProjectMemberView) => m.userId.toLowerCase() === userId.toLowerCase();

  const setRole = useMutation({
    mutationFn: ({ row, role }: { row: Access; role: AssignableProjectRole }) =>
      access.setRole[row.kind](row.id, role),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (row: Access) => access.remove[row.kind](row.id),
    onSuccess: () => setRemoveOpen(false),
    onSettled: refresh,
  });
  const transfer = useMutation({
    mutationFn: async (member: ProjectMemberView) =>
      ok(
        await api.api.projects[":projectId"].owner.$post(
          { param: { projectId: project.id }, json: { userId: member.userId } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => setTransferring(null),
    onSettled: refresh,
  });
  const closeRemove = () => {
    if (!remove.isPending) {
      setRemoveOpen(false);
      remove.reset();
    }
  };
  const closeTransfer = () => {
    if (!transfer.isPending) {
      setTransferring(null);
      transfer.reset();
    }
  };

  // The owner first, then people, groups and rules, each in the order the API lists them.
  const people: Access[] = (members.data ?? [])
    .map((m) => ({
      kind: "person" as const,
      id: m.userId,
      name: nameOf(m),
      role: m.role,
      member: m,
    }))
    .sort((a, b) => Number(b.role === "owner") - Number(a.role === "owner"));
  const rows: Access[] = [
    ...people,
    ...(groups.data ?? []).map((g) => ({
      kind: "group" as const,
      id: g.groupId,
      name: g.displayName,
      role: g.role,
      grant: g,
    })),
    ...(rules.data ?? []).map((r) => ({
      kind: "rule" as const,
      id: r.ruleId,
      name: r.name,
      role: r.role,
      grant: r,
    })),
  ];
  const loading = [members, groups, rules].some((q) => !q.data && !q.isError);
  // A kind whose list failed has no count and no "none" message: the error says why.
  const known: Record<Filter, boolean> = {
    all: members.isSuccess && groups.isSuccess && rules.isSuccess,
    person: members.isSuccess,
    group: groups.isSuccess,
    rule: rules.isSuccess,
  };
  const counts: Record<Filter, number> = {
    all: rows.length,
    person: people.length,
    group: groups.data?.length ?? 0,
    rule: rules.data?.length ?? 0,
  };
  // Filters only when there is more than people to tell apart, or could be; without them the
  // list always shows everything (a filter left over from before never hides rows).
  const filtering = counts.group + counts.rule > 0 || manageGrants;
  const active: Filter = filtering ? filter : "all";
  const shown = active === "all" ? rows : rows.filter((r) => r.kind === active);
  const manages = (row: Access) => (row.kind === "person" ? manageMembers : manageGrants);
  // A trailing column only when some row may have actions, so read-only lists end at the role.
  const actionsColumn = manageMembers || manageGrants;
  // One notice per distinct failure (offline, all three fail alike).
  const errors = [
    ...new Set([members.error, groups.error, rules.error].flatMap((e) => (e ? [e.message] : []))),
  ];
  const mutationError = setRole.error;

  return (
    <SettingsSection
      id="members"
      level={props.level}
      action={
        manageMembers || manageGrants ? (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" strokeWidth={1.75} />
            {t("settings.members.add")}
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-3">
        {errors.map((message) => (
          <Notice key={message} tone="danger">
            {message}
          </Notice>
        ))}
        {/* Without the people, a partial list would read as "no members": show only the error. */}
        {members.isError ? null : loading ? (
          <Skeleton className="h-36 w-full rounded-lg" />
        ) : (
          <>
            {filtering ? (
              <FilterChips<Filter>
                label={t("settings.members.filter")}
                value={active}
                onChange={setFilter}
                options={(["all", "person", "group", "rule"] as const).map((kind) => ({
                  value: kind,
                  label: t(`settings.members.kinds.${kind}`),
                  ...(known[kind] ? { count: counts[kind] } : {}),
                }))}
              />
            ) : null}
            {shown.length === 0 && !known[active] ? null : shown.length === 0 ? (
              <p className="rounded-lg border border-line px-4 py-6 text-sm text-fg-secondary">
                {t(`settings.members.empty.${active}`)}
              </p>
            ) : (
              <ul
                data-esi-content="people"
                aria-label={t("settings.sections.members")}
                className="@container divide-y divide-line rounded-lg border border-line bg-surface-raised"
              >
                {shown.map((row) => {
                  const owner = row.kind === "person" && row.role === "owner";
                  const breakGlass = row.kind === "person" && row.member.viaBreakGlass;
                  // Break-glass access is always Reader: shown, never changed (the server
                  // refuses it). The owner's role moves only by a transfer.
                  const editable = manages(row) && !owner && !breakGlass;
                  const actions = manages(row) && !owner;
                  return (
                    <ListRow
                      key={`${row.kind}:${row.id}`}
                      leading={
                        row.kind === "person" ? (
                          row.member.displayName ? (
                            <Avatar name={row.member.displayName} />
                          ) : (
                            // No name to take initials from: never initials of an ID.
                            <Avatar icon={UserRound} />
                          )
                        ) : (
                          <Avatar icon={KIND_ICON[row.kind]} />
                        )
                      }
                      title={
                        <>
                          {row.name}
                          {row.kind === "person" && isMe(row.member) ? (
                            <span className="text-fg-muted"> ({t("members.you")})</span>
                          ) : null}
                        </>
                      }
                      subtitle={<Subtitle row={row} />}
                      notes={
                        <Notes row={row} project={project} ownerOnlyNote={props.ownerOnlyNote} />
                      }
                      trailing={
                        <>
                          {editable ? (
                            <RolePicker
                              aria-label={t("settings.members.roleOf", { name: row.name })}
                              value={row.role}
                              size="sm"
                              disabled={setRole.isPending}
                              onChange={(role) => {
                                remove.reset();
                                setRole.mutate({ row, role });
                              }}
                            />
                          ) : (
                            <RoleLabel
                              role={row.role}
                              icon={
                                owner ? (
                                  <Crown
                                    aria-hidden="true"
                                    className="size-3.5"
                                    strokeWidth={1.75}
                                  />
                                ) : undefined
                              }
                            />
                          )}
                          {actions ? (
                            <RowActions
                              name={row.name}
                              transferable={row.kind === "person" && mayTransfer && !breakGlass}
                              onTransfer={(from) => {
                                if (row.kind === "person") {
                                  opener.current = from;
                                  setRole.reset();
                                  setTransferring(row.member);
                                }
                              }}
                              onRemove={(from) => {
                                opener.current = from;
                                setRole.reset();
                                setRemoving(row);
                                setRemoveOpen(true);
                              }}
                            />
                          ) : actionsColumn ? (
                            // Keeps the role column aligned with rows that have an actions button.
                            <span aria-hidden="true" className="size-8 shrink-0" />
                          ) : null}
                        </>
                      }
                    />
                  );
                })}
              </ul>
            )}
          </>
        )}
        {mutationError ? (
          <InlineError>
            {projectRefusal(
              mutationError,
              setRole.variables?.row.kind === "person" ? "member" : undefined,
            )}
          </InlineError>
        ) : null}
        {project.visibility !== "private" ? (
          <p className="text-sm text-fg-secondary">
            {t(`members.audienceNote.${project.visibility}`)}
          </p>
        ) : null}
      </div>
      <AddAccessDialog
        open={adding}
        onClose={() => setAdding(false)}
        projectId={project.id}
        access={access}
        manageMembers={manageMembers}
        manageGrants={manageGrants}
        principals={principals.data}
        principalsError={principals.error?.message}
        granted={rows}
        grantsKnown={{
          group: groups.isSuccess && !groups.isFetching,
          rule: rules.isSuccess && !rules.isFetching,
        }}
        grantsError={{ group: groups.error?.message, rule: rules.error?.message }}
        onAdded={refresh}
      />
      <AppDialog
        open={removeOpen}
        onClose={closeRemove}
        returnFocus={opener.current}
        title={removing ? t("settings.members.removeTitle", { name: removing.name }) : ""}
        description={removing ? t(`settings.members.removeBody.${removing.kind}`) : ""}
      >
        <div className="flex flex-col gap-4">
          {remove.isError ? (
            <Notice tone="danger">
              {projectRefusal(remove.error, removing?.kind === "person" ? "member" : undefined)}
            </Notice>
          ) : null}
          <DialogFooter>
            <Button variant="outline" disabled={remove.isPending} onClick={closeRemove}>
              {t("settings.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => removing && remove.mutate(removing)}
            >
              {removing ? t(`settings.members.removeConfirm.${removing.kind}`) : null}
            </Button>
          </DialogFooter>
        </div>
      </AppDialog>
      <AppDialog
        open={transferring !== null}
        onClose={closeTransfer}
        returnFocus={opener.current}
        title={
          transferring ? t("settings.members.transferTitle", { name: nameOf(transferring) }) : ""
        }
        description={
          transferring ? t("settings.members.transferBody", { name: nameOf(transferring) }) : ""
        }
      >
        <div className="flex flex-col gap-4">
          <ul
            data-esi-content="people"
            className="list-disc space-y-1 pl-5 text-sm text-fg-secondary"
          >
            <li>{t("settings.members.transferPoints.owner")}</li>
            <li>{t("settings.members.transferPoints.you")}</li>
          </ul>
          {transfer.isError ? (
            <Notice tone="danger">{projectRefusal(transfer.error, "member")}</Notice>
          ) : null}
          <DialogFooter>
            <Button variant="outline" disabled={transfer.isPending} onClick={closeTransfer}>
              {t("settings.cancel")}
            </Button>
            <Button
              disabled={transfer.isPending}
              onClick={() => transferring && transfer.mutate(transferring)}
            >
              {transfer.isPending
                ? t("settings.members.transferring")
                : t("settings.members.transferConfirm")}
            </Button>
          </DialogFooter>
        </div>
      </AppDialog>
    </SettingsSection>
  );
}

const nameOf = (m: ProjectMemberView) => m.displayName ?? shortId(m.userId);

/**
 * The row's one-line subtitle: the person's e-mail (only member managers get it), else since
 * when; how many people; or what kind of rule.
 */
function Subtitle({ row }: { row: Access }) {
  const { t } = useTranslation();
  if (row.kind === "person") {
    if (row.member.viaBreakGlass) {
      return t("settings.members.breakGlass");
    }
    return row.member.email && row.member.email !== row.member.displayName
      ? row.member.email
      : t("settings.members.memberSince", { date: formatDate(row.member.createdAt) });
  }
  if (row.kind === "group") {
    return t("members.groupMembers", { count: row.grant.memberCount });
  }
  return t("settings.members.rule");
}

/** What else the row must say: who may transfer, a guest the visibility lifts, exclusions. */
function Notes(props: {
  row: Access;
  project: Pick<ProjectDetail, "visibility">;
  ownerOnlyNote: boolean;
}) {
  const { row, project } = props;
  const { t } = useTranslation();
  if (row.kind === "person" && row.role === "owner") {
    return props.ownerOnlyNote ? (
      <span className="text-xs text-fg-muted">{t("settings.members.ownerOnly")}</span>
    ) : null;
  }
  if (row.kind === "person" && row.role === "guest" && project.visibility !== "private") {
    return (
      <span className="text-xs text-warning-fg">
        {t("settings.members.guestLifted", { role: t("projectRoles.reader.name") })}
      </span>
    );
  }
  if (row.kind === "group" && row.grant.excludedCount > 0) {
    return (
      <span className="text-xs text-warning-fg tabular-nums">
        {t("members.excluded", { count: row.grant.excludedCount })}
      </span>
    );
  }
  return null;
}

/** The "…" menu of a row: transfer ownership (to a person, owner only) and removal. */
function RowActions(props: {
  name: string;
  transferable: boolean;
  onTransfer: (from: HTMLElement | null) => void;
  onRemove: (from: HTMLElement | null) => void;
}) {
  const { t } = useTranslation();
  const trigger = useRef<HTMLButtonElement>(null);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          ref={trigger}
          variant="ghost"
          size="icon-sm"
          aria-label={t("settings.members.actionsOf", { name: props.name })}
          className="shrink-0 text-fg-muted hover:text-fg"
        >
          <Ellipsis aria-hidden="true" strokeWidth={1.75} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        {props.transferable ? (
          <DropdownMenuItem onSelect={() => props.onTransfer(trigger.current)}>
            <Crown aria-hidden="true" strokeWidth={1.75} />
            {t("settings.members.transfer")}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem variant="destructive" onSelect={() => props.onRemove(trigger.current)}>
          <UserMinus aria-hidden="true" strokeWidth={1.75} />
          {t("settings.members.remove")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
